// Demo-only mock data (active when REACT_APP_DEMO_ACCOUNT_PREFIX is set).
//
// Nothing here is written to the backend. Two pieces:
//  - `activity` (date -> seconds) drives every headline number (hours
//    completed, hours left, "this month"). It is generated CATALOG-FREE so it
//    always populates, and the SAME generator feeds both the Account dashboard
//    and the home widget -> the two never disagree.
//  - `watch_history` (the "videos watched" list) needs real video ids, so it is
//    built from the live catalog, but falls back to a default length if the
//    catalog doesn't report one (so it still generates).
//
// All six months are populated INCLUDING the current month (pro-rated to today)
// so the default view is never empty; real live watch-time still merges on top
// of the current month, so tracking is visibly additive during the demo.
// Deterministic (seeded) so the numbers are stable across reloads.

const DEFAULT_VIDEO_LEN = 600; // fallback ~10 min when the catalog omits length

// mulberry32: tiny deterministic PRNG. Same seed -> same sequence.
const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// FNV-1a string hash -> 32-bit seed.
const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const pad2 = (n) => String(n).padStart(2, "0");

// YYYY-MM-DD for a given month offset (0 = current month) and day-of-month.
const dateKey = (monthOffset, day) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthOffset);
  d.setDate(day);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const daysInMonthOffset = (monthOffset) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthOffset + 1);
  d.setDate(0);
  return d.getDate();
};

// Never schedule a watch in the future; pro-rate the current month by how much
// of it has elapsed so the in-progress month reads as "on track".
const monthWindow = (offset) => {
  const today = new Date().getDate();
  const maxDay = offset === 0 ? today : daysInMonthOffset(offset);
  const frac = offset === 0 ? today / daysInMonthOffset(0) : 1;
  return {maxDay, frac};
};

// Demo-only: accounts that should see the Admin panel in the demo build WITHOUT
// a real Cognito `admin` group. Comma-separated emails/ids in
// REACT_APP_DEMO_ADMIN_EMAILS, matched case-insensitively against the logged-in
// customer_id. Only consulted in demo mode; unset/empty = no extra admins. Safe
// because the demo admin panel renders the in-memory mock cohort, not the real
// (server-gated) /get-contacts data.
const DEMO_ADMIN_EMAILS = (process.env.REACT_APP_DEMO_ADMIN_EMAILS || "")
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

export const isDemoAdmin = (customerId) => {
  if (!process.env.REACT_APP_DEMO_ACCOUNT_PREFIX) return false;
  const id = (customerId || "").trim().toLowerCase();
  return id !== "" && DEMO_ADMIN_EMAILS.includes(id);
};

// Catalog-FREE activity map (date -> {secondsActive, totalVideoSeconds, ...}).
// monthTargets: [{offset, hours}] target VIDEO hours for that month.
const demoActivityMap = (monthTargets, seed) => {
  const activity = {};
  const rng = mulberry32(seed);
  monthTargets.forEach(({offset, hours}) => {
    const {maxDay, frac} = monthWindow(offset);
    let remaining = Math.round((hours || 0) * frac * 3600);
    if (remaining <= 0) return;
    let guard = 0;
    while (remaining > 120 && guard < 2000) {
      guard += 1;
      const day = 1 + Math.floor(rng() * maxDay);
      const dk = dateKey(offset, day);
      const chunk = Math.min(remaining, 300 + Math.floor(rng() * 2400)); // 5-45m
      const a = activity[dk] || {
        mouseMoves: 0,
        clicks: 0,
        secondsActive: 0,
        totalVideoSeconds: 0,
      };
      a.totalVideoSeconds += chunk;
      a.secondsActive += chunk + Math.round(chunk * (0.12 + rng() * 0.28));
      a.mouseMoves += Math.round(chunk / 4);
      a.clicks += 3 + Math.floor(rng() * 8);
      activity[dk] = a;
      remaining -= chunk;
    }
  });
  return activity;
};

// Deterministically pick `count` distinct videos from the catalog.
const pickVideos = (usable, count, rng) => {
  const pool = [...usable];
  const picked = [];
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i += 1) {
    const idx = Math.floor(rng() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }
  return picked;
};

// Catalog-based watch_history (videoId -> {lastPosition, lastWatched,
// dailyBreakdown}). Falls back to a default length when the catalog omits one,
// so it still generates against a real (but length-less) catalog.
const buildWatchHistory = (videos, monthTargets, seed) => {
  const watch_history = {};
  const usable = videos || [];
  if (usable.length === 0) return watch_history;
  const rng = mulberry32(seed);

  monthTargets.forEach(({offset, hours}) => {
    const {maxDay, frac} = monthWindow(offset);
    let remaining = Math.round((hours || 0) * frac * 3600);
    if (remaining <= 0) return;
    const uniqueCount = 12 + Math.floor(rng() * 20);
    const picks = pickVideos(usable, uniqueCount, rng);
    if (picks.length === 0) return;

    let guard = 0;
    while (remaining > 120 && guard < 2500) {
      guard += 1;
      const v = picks[Math.floor(rng() * picks.length)];
      const len = Math.round(Number(v.videoLength)) || DEFAULT_VIDEO_LEN;
      const full = rng() < 0.6;
      const watched = full
        ? len
        : Math.max(60, Math.round(len * (0.3 + rng() * 0.5)));
      const secs = Math.min(watched, remaining);
      const day = 1 + Math.floor(rng() * maxDay);
      const dk = dateKey(offset, day);

      const id = String(v.id);
      const wh = watch_history[id] || {
        lastPosition: 0,
        lastWatched: null,
        dailyBreakdown: {},
      };
      wh.dailyBreakdown[dk] = (wh.dailyBreakdown[dk] || 0) + secs;
      wh.lastPosition = Math.max(wh.lastPosition, full ? len : watched);
      const iso = `${dk}T${pad2(8 + Math.floor(rng() * 10))}:${pad2(
        Math.floor(rng() * 60),
      )}:00.000Z`;
      if (!wh.lastWatched || dk >= wh.lastWatched.slice(0, 10)) {
        wh.lastWatched = iso;
      }
      watch_history[id] = wh;
      remaining -= secs;
    }
  });

  return watch_history;
};

// Most recent YYYY-MM-DD across an activity map -> a timestamp for the table.
const latestActive = (activity) => {
  const keys = Object.keys(activity || {}).sort();
  return keys.length ? `${keys[keys.length - 1]}T15:00:00.000Z` : null;
};

// ~80h/mo = the work requirement. Profiles tell a "mixed, realistic caseload"
// story. Index 0 = current month .. index 5 = five months ago.
const DEMO_PROFILES = [
  {first: "Maria", last: "Gonzalez", hours: [82, 79, 84, 80, 81, 77]},
  {first: "James", last: "Whitfield", hours: [58, 61, 55, 60, 52, 57]},
  {first: "Aaliyah", last: "Roberts", hours: [46, 34, 25, 17, 9, 4]},
  {first: "Devon", last: "Carter", hours: [12, 28, 44, 56, 61, 58]},
  {first: "Sophie", last: "Nguyen", hours: [21, 7, 3, 0, 0, 0]},
  {first: "Marcus", last: "Bell", hours: [44, 41, 47, 39, 43, 45]},
  {first: "Elena", last: "Petrova", hours: [11, 8, 14, 6, 12, 9]},
  {first: "Tyler", last: "Hoffman", hours: [69, 63, 42, 22, 13, 7]},
  {first: "Priya", last: "Sharma", hours: [27, 15, 33, 4, 40, 6]},
  {first: "Andre", last: "Jackson", hours: [86, 83, 80, 85, 79, 82]},
];

// The logged-in user's own six months. Current month included so the default
// view is populated; live watch-time still merges on top of it.
const SELF_MONTHS = [
  {offset: 0, hours: 40},
  {offset: 1, hours: 47},
  {offset: 2, hours: 38},
  {offset: 3, hours: 54},
  {offset: 4, hours: 31},
  {offset: 5, hours: 49},
];
const SELF_SEED = hashSeed("demo-self");

// Name pools for the auto-generated bulk of the cohort (beyond the 10 curated
// profiles). Deterministic pick by index -> stable names across reloads.
const FIRST_NAMES = [
  "Liam",
  "Olivia",
  "Noah",
  "Emma",
  "Oliver",
  "Ava",
  "Elijah",
  "Sophia",
  "Mateo",
  "Isabella",
  "Lucas",
  "Mia",
  "Levi",
  "Amelia",
  "Ezra",
  "Harper",
  "Asher",
  "Evelyn",
  "Leo",
  "Luna",
  "Ethan",
  "Camila",
  "Sebastian",
  "Gianna",
  "Mason",
  "Layla",
  "Michael",
  "Aria",
  "Daniel",
  "Ella",
  "Jack",
  "Nora",
  "Aiden",
  "Hazel",
  "Samuel",
  "Zoey",
  "Henry",
  "Riley",
  "Owen",
  "Willow",
  "Wyatt",
  "Naomi",
  "Carlos",
  "Nina",
  "Malik",
  "Priya",
  "Diego",
  "Fatima",
  "Andre",
  "Grace",
  "Jamal",
  "Sofia",
  "Kenji",
  "Maya",
  "Omar",
  "Chloe",
  "Hassan",
  "Ruby",
  "Tariq",
  "Iris",
  "Darnell",
  "Lucia",
  "Nikolai",
  "Yara",
  "Kwame",
  "Anaya",
  "Rafael",
  "Simone",
  "Dmitri",
  "Talia",
  "Terrence",
  "Bianca",
  "Reza",
  "Elise",
  "Kofi",
  "Adriana",
  "Santiago",
  "Farah",
  "Deshawn",
  "Ingrid",
];
const LAST_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Garcia",
  "Miller",
  "Davis",
  "Rodriguez",
  "Martinez",
  "Hernandez",
  "Lopez",
  "Wilson",
  "Anderson",
  "Thomas",
  "Taylor",
  "Moore",
  "Jackson",
  "Martin",
  "Lee",
  "Perez",
  "Thompson",
  "White",
  "Harris",
  "Sanchez",
  "Clark",
  "Ramirez",
  "Lewis",
  "Robinson",
  "Walker",
  "Young",
  "Allen",
  "King",
  "Wright",
  "Scott",
  "Torres",
  "Nguyen",
  "Hill",
  "Flores",
  "Green",
  "Adams",
  "Nelson",
  "Baker",
  "Hall",
  "Rivera",
  "Campbell",
  "Mitchell",
  "Roberts",
  "Gomez",
  "Phillips",
  "Evans",
  "Turner",
  "Diaz",
  "Parker",
  "Cruz",
  "Edwards",
  "Collins",
  "Reyes",
  "Stewart",
  "Morris",
  "Morales",
  "Murphy",
  "Cook",
  "Rogers",
  "Gutierrez",
  "Ortiz",
  "Morgan",
  "Cooper",
  "Bailey",
  "Reed",
  "Kelly",
  "Howard",
  "Ramos",
  "Kim",
  "Cox",
  "Ward",
  "Brooks",
  "Chavez",
  "Okafor",
  "Patel",
  "Osei",
];

// Deterministic 6-month hours for a generated user, following one of a few
// archetypes so the cohort tells a mixed, realistic story.
const genHours = (rng) => {
  const archetype = Math.floor(rng() * 5);
  const base = 5 + Math.floor(rng() * 78);
  const hours = [];
  for (let m = 0; m < 6; m += 1) {
    let h;
    if (archetype === 0)
      h = base + Math.round((rng() - 0.5) * 14); // steady
    else if (archetype === 1)
      h = base - m * 8 + Math.round((rng() - 0.5) * 10); // ramping up
    else if (archetype === 2)
      h = base - (5 - m) * 8 + Math.round((rng() - 0.5) * 10); // dropped off
    else if (archetype === 3)
      h = rng() < 0.4 ? 0 : base + Math.round((rng() - 0.5) * 20); // sporadic
    else h = base + Math.round((rng() - 0.5) * 24); // noisy
    hours.push(Math.max(0, Math.min(95, h)));
  }
  return hours;
};

const DEMO_USER_COUNT = 100;

// The fake accounts the admin (Nelson) browses. The first 10 are the curated
// named profiles; the rest are generated. All six months populated.
export const buildDemoUsers = (videos) => {
  const users = [];
  for (let i = 0; i < DEMO_USER_COUNT; i += 1) {
    let first;
    let last;
    let hoursByMonth;
    if (i < DEMO_PROFILES.length) {
      const p = DEMO_PROFILES[i];
      first = p.first;
      last = p.last;
      hoursByMonth = p.hours;
    } else {
      const nameRng = mulberry32(hashSeed(`name-${i}`));
      first = FIRST_NAMES[Math.floor(nameRng() * FIRST_NAMES.length)];
      last = LAST_NAMES[Math.floor(nameRng() * LAST_NAMES.length)];
      hoursByMonth = genHours(nameRng);
    }
    const customer_id = `+1208555${String(i + 1).padStart(4, "0")}`;
    const seed = hashSeed(customer_id);
    const monthTargets = hoursByMonth.map((hours, offset) => ({offset, hours}));
    const activity = demoActivityMap(monthTargets, seed);
    const watch_history = buildWatchHistory(
      videos,
      monthTargets,
      seed ^ 0x9e37,
    );
    users.push({
      customer_id,
      customer_firstname: first,
      customer_lastname: last,
      timestamp: latestActive(activity),
      activity,
      watch_history,
    });
  }
  return users;
};

// Merge the demo activity map into a real activity map (for the home widget).
// Real days win on collision so the live current day is never overwritten.
export const mergeDemoActivity = (realActivity) => ({
  ...demoActivityMap(SELF_MONTHS, SELF_SEED),
  ...(realActivity || {}),
});

// Overlay six months of demo data onto the logged-in user for the Account
// dashboard. Activity comes from the SAME generator the home widget uses, so
// the two agree. Real days/entries win on collision (current month stays live).
export const overlayDemoMonths = (user, videos) => {
  const base = user || {};
  const activity = demoActivityMap(SELF_MONTHS, SELF_SEED);
  const watch_history = buildWatchHistory(
    videos,
    SELF_MONTHS,
    SELF_SEED ^ 0x9e37,
  );

  const mergedActivity = {...activity, ...(base.activity || {})};

  const mergedHistory = {...(base.watch_history || {})};
  Object.entries(watch_history).forEach(([id, mock]) => {
    const real = mergedHistory[id];
    if (!real) {
      mergedHistory[id] = mock;
      return;
    }
    mergedHistory[id] = {
      lastPosition: Math.max(real.lastPosition || 0, mock.lastPosition || 0),
      lastWatched:
        (real.lastWatched || "") > (mock.lastWatched || "")
          ? real.lastWatched
          : mock.lastWatched,
      dailyBreakdown: {...mock.dailyBreakdown, ...(real.dailyBreakdown || {})},
    };
  });

  return {...base, activity: mergedActivity, watch_history: mergedHistory};
};

// Merge demo watch_history into a real one for DISPLAY (e.g. the Recently
// Watched sidebar). Real entries win on collision. Never fed into synced state,
// so nothing is written to the backend.
export const overlayDemoWatchHistory = (realWatchHistory, videos) => {
  const mock = buildWatchHistory(videos, SELF_MONTHS, SELF_SEED ^ 0x9e37);
  const merged = {...(realWatchHistory || {})};
  Object.entries(mock).forEach(([id, m]) => {
    const real = merged[id];
    if (!real) {
      merged[id] = m;
      return;
    }
    merged[id] = {
      lastPosition: Math.max(real.lastPosition || 0, m.lastPosition || 0),
      lastWatched:
        (real.lastWatched || "") > (m.lastWatched || "")
          ? real.lastWatched
          : m.lastWatched,
      dailyBreakdown: {...m.dailyBreakdown, ...(real.dailyBreakdown || {})},
    };
  });
  return merged;
};

// Back-compat alias (older import name).
export const overlayDemoPastMonths = overlayDemoMonths;
